import { request } from "./http";
import { diaryMoodBadge, diaryMoodLabel } from "../utils/diary";

function asArray(value) {
  return Array.isArray(value) ? value : [];
}

function asObject(value) {
  return value && typeof value === "object" ? value : {};
}

function normalizeDiaryErrorMessage(error, fallback = "Не удалось выполнить запрос дневника") {
  const status = Number(error?.status || 0);
  const raw = String(error?.message || error?.error || error?.code || "")
    .trim()
    .toUpperCase();
  const readable = String(error?.message || "").trim();

  if (raw.includes("DIARIES_DISABLED")) {
    return "Раздел дневника сейчас отключен на сервере. Обратитесь к администратору backend.";
  }
  if (raw.includes("DIARY_ENTRY_NOT_FOUND")) return "Запись дневника не найдена.";
  if (raw.includes("DIARY_ACCESS_DENIED")) return "Нет доступа к этой записи дневника.";
  if (raw.includes("DIARY_READ_LOGS_DISABLED")) return "История чтения для дневника сейчас недоступна.";
  if (
    raw.includes("NETWORKERROR") ||
    raw.includes("FAILED TO FETCH") ||
    raw.includes("LOAD FAILED") ||
    raw.includes("ERR_CONNECTION_REFUSED") ||
    raw.includes("ERR_NETWORK") ||
    raw.includes("FETCH FAILED")
  ) {
    return "Не удалось подключиться к серверу дневника. Проверьте, что backend запущен и доступен.";
  }

  if (status === 401 || status === 403) return "Нужно авторизоваться, чтобы работать с дневником.";
  if (status === 404) return "API дневника не найдено на сервере.";
  return readable || fallback;
}

function decorateDiaryError(error, fallback) {
  const next = error instanceof Error ? error : new Error(String(error || fallback || "Ошибка дневника"));
  const mapped = normalizeDiaryErrorMessage(error, fallback);
  next.message = mapped;
  if (error && typeof error === "object") {
    Object.keys(error).forEach((key) => {
      if (!(key in next)) next[key] = error[key];
    });
  }
  const raw = String(error?.message || error?.error || error?.code || "")
    .trim()
    .toUpperCase();
  if (raw.includes("DIARIES_DISABLED")) {
    next.code = "DIARIES_DISABLED";
    next.diaryDisabled = true;
  }
  if (Number(error?.status || 0) === 404) {
    next.code = next.code || "DIARY_API_NOT_FOUND";
    next.diaryUnavailable = true;
  }
  return next;
}

function canTryFallback(error) {
  const status = Number(error?.status || 0);
  const raw = String(error?.message || error?.error || error?.code || "")
    .trim()
    .toUpperCase();
  if (raw.includes("DIARIES_DISABLED")) return false;
  return status === 404 || status === 405 || status === 501;
}

async function requestFirstAvailable(paths = [], makeCall, fallbackErrorText) {
  let lastError = null;
  for (const path of paths) {
    try {
      return await makeCall(path);
    } catch (error) {
      if (!canTryFallback(error)) throw decorateDiaryError(error, fallbackErrorText);
      lastError = error;
    }
  }
  throw decorateDiaryError(lastError, fallbackErrorText);
}

function readBoolean(value, fallback = false) {
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value !== 0;
  const normalized = String(value ?? "")
    .trim()
    .toLowerCase();
  if (!normalized) return fallback;
  if (["1", "true", "yes", "y", "on"].includes(normalized)) return true;
  if (["0", "false", "no", "n", "off"].includes(normalized)) return false;
  return fallback;
}

function extractItems(raw) {
  if (Array.isArray(raw)) return raw;

  const obj = asObject(raw);
  if (Array.isArray(obj.content)) return obj.content;
  if (Array.isArray(obj.items)) return obj.items;
  if (Array.isArray(obj.items?.content)) return obj.items.content;
  if (Array.isArray(obj.item?.items)) return obj.item.items;
  if (Array.isArray(obj.item?.content)) return obj.item.content;
  if (Array.isArray(obj.item)) return obj.item;
  return [];
}

function extractPage(raw) {
  const obj = asObject(raw);
  if (Array.isArray(obj.content)) return obj;
  if (obj.items && typeof obj.items === "object" && Array.isArray(obj.items.content)) return obj.items;
  if (obj.item && typeof obj.item === "object" && Array.isArray(obj.item.content)) return obj.item;
  return { content: extractItems(raw), totalElements: extractItems(raw).length };
}

function buildPageResult(pageLike, fallbackPage = 0, fallbackSize = 20) {
  const content = extractItems(pageLike);
  const page = Number.isFinite(Number(pageLike?.number)) ? Number(pageLike.number) : fallbackPage;
  const size = Number.isFinite(Number(pageLike?.size)) ? Number(pageLike.size) : fallbackSize;
  const totalElements = Number.isFinite(Number(pageLike?.totalElements))
    ? Number(pageLike.totalElements)
    : content.length;
  const totalPages = Number.isFinite(Number(pageLike?.totalPages))
    ? Number(pageLike.totalPages)
    : Math.max(1, Math.ceil(totalElements / Math.max(1, size)));

  return {
    page,
    size,
    totalElements,
    totalPages,
    hasMore: page + 1 < totalPages,
  };
}

export function normalizeDiaryEntry(item = {}) {
  const raw = asObject(item);
  const moodScore = Number(raw.moodScore);
  const safeMoodScore = Number.isFinite(moodScore) ? moodScore : null;
  const title = String(raw.title || "").trim();
  const content = String(raw.content || raw.text || raw.body || "").trim();

  const sharedValue =
    raw.shareWithGrantedPsychologists ??
    raw.shareWithPsychologists ??
    raw.sharedWithPsychologist ??
    raw.sharedWithPsychologists ??
    raw.shared ??
    raw.isShared;

  return {
    id: raw.id ?? raw.entryId ?? null,
    ownerUserId: raw.ownerUserId ?? raw.ownerId ?? null,
    title: title || "Запись в дневнике",
    content,
    text: content,
    moodScore: safeMoodScore,
    moodLabel: diaryMoodLabel(safeMoodScore),
    moodBadge: diaryMoodBadge(safeMoodScore),
    entryDate: raw.entryDate || raw.date || null,
    shareWithGrantedPsychologists: readBoolean(sharedValue, false),
    createdAt: raw.createdAt || raw.createdWhen || null,
    updatedAt: raw.updatedAt || raw.updatedWhen || raw.lastModified || null,
  };
}

export function normalizeDiaryAccess(item = {}) {
  const raw = asObject(item);
  return {
    id: raw.id ?? null,
    ownerUserId: raw.ownerUserId ?? null,
    psychologistUserId: raw.psychologistUserId ?? raw.userId ?? null,
    psychologistName: String(raw.psychologistName || raw.name || "Психолог"),
    comment: String(raw.comment || "").trim(),
    grantedAt: raw.grantedAt || raw.createdAt || raw.createdWhen || null,
    updatedAt: raw.updatedAt || raw.updatedWhen || raw.lastModified || null,
  };
}

export function normalizeDiaryReadLog(item = {}) {
  const raw = asObject(item);
  return {
    id: raw.id ?? null,
    entryId: raw.entryId ?? null,
    readerUserId: raw.readerUserId ?? raw.userId ?? null,
    readerName: String(raw.readerName || raw.name || "Специалист"),
    readerRole: String(raw.readerRole || raw.role || "").trim(),
    createdAt: raw.createdAt || raw.createdWhen || null,
  };
}

function toDiaryPayload(payload = {}) {
  const content = String(payload.content || payload.text || "").trim();
  const title = String(payload.title || "").trim();
  const moodScore = Number(payload.moodScore);

  return {
    title: title || null,
    content,
    moodScore: Number.isFinite(moodScore) ? moodScore : null,
    entryDate: payload.entryDate || null,
    shareWithGrantedPsychologists:
      payload.shareWithGrantedPsychologists === undefined
        ? false
        : readBoolean(payload.shareWithGrantedPsychologists, false),
  };
}

export const diaryApi = {
  async listEntries({ q = "", page = 0, size = 50 } = {}) {
    const search = new URLSearchParams();
    search.set("page", String(Math.max(0, Number(page) || 0)));
    search.set("size", String(Math.max(1, Number(size) || 50)));
    if (String(q || "").trim()) search.set("q", String(q).trim());

    try {
      const response = await requestFirstAvailable(
        ["/api/me/diary/entries", "/api/me/content/diary/entries"],
        (path) => request(`${path}?${search.toString()}`),
        "Не удалось загрузить записи дневника"
      );
      const pageLike = extractPage(response?.items ?? response);
      const meta = buildPageResult(pageLike, page, size);

      return {
        ...meta,
        items: asArray(pageLike?.content).map(normalizeDiaryEntry),
      };
    } catch (error) {
      throw decorateDiaryError(error, "Не удалось загрузить записи дневника");
    }
  },

  async createEntry(payload = {}) {
    try {
      const response = await requestFirstAvailable(
        ["/api/me/diary/entries", "/api/me/content/diary/entries"],
        (path) =>
          request(path, {
            method: "POST",
            json: toDiaryPayload(payload),
          }),
        "Не удалось создать запись дневника"
      );
      return normalizeDiaryEntry(response?.item ?? response);
    } catch (error) {
      throw decorateDiaryError(error, "Не удалось создать запись дневника");
    }
  },

  async updateEntry(entryId, payload = {}) {
    const id = String(entryId ?? "").trim();
    if (!id) throw new Error("entryId is required");

    try {
      const response = await requestFirstAvailable(
        [
          `/api/me/diary/entries/${encodeURIComponent(id)}`,
          `/api/me/content/diary/entries/${encodeURIComponent(id)}`,
        ],
        (path) =>
          request(path, {
            method: "PUT",
            json: toDiaryPayload(payload),
          }),
        "Не удалось сохранить запись дневника"
      );
      return normalizeDiaryEntry(response?.item ?? response);
    } catch (error) {
      throw decorateDiaryError(error, "Не удалось сохранить запись дневника");
    }
  },

  async deleteEntry(entryId) {
    const id = String(entryId ?? "").trim();
    if (!id) throw new Error("entryId is required");
    try {
      return await requestFirstAvailable(
        [
          `/api/me/diary/entries/${encodeURIComponent(id)}`,
          `/api/me/content/diary/entries/${encodeURIComponent(id)}`,
        ],
        (path) => request(path, { method: "DELETE" }),
        "Не удалось удалить запись дневника"
      );
    } catch (error) {
      throw decorateDiaryError(error, "Не удалось удалить запись дневника");
    }
  },

  async listAccesses() {
    try {
      const response = await requestFirstAvailable(
        ["/api/me/diary/accesses", "/api/me/content/diary/accesses"],
        (path) => request(path),
        "Не удалось загрузить доступы к дневнику"
      );
      return extractItems(response?.items ?? response).map(normalizeDiaryAccess);
    } catch (error) {
      throw decorateDiaryError(error, "Не удалось загрузить доступы к дневнику");
    }
  },

  async grantAccess(psychologistUserId, comment = "") {
    const id = String(psychologistUserId ?? "").trim();
    if (!id) throw new Error("psychologistUserId is required");
    try {
      const response = await requestFirstAvailable(
        ["/api/me/diary/accesses", "/api/me/diary/access", "/api/me/content/diary/accesses"],
        (path) =>
          request(path, {
            method: "POST",
            json: { psychologistUserId: id, comment: String(comment || "").trim() || null },
          }),
        "Не удалось выдать доступ к дневнику"
      );
      return normalizeDiaryAccess(response?.item ?? response);
    } catch (error) {
      throw decorateDiaryError(error, "Не удалось выдать доступ к дневнику");
    }
  },

  async revokeAccess(psychologistUserId) {
    const id = String(psychologistUserId ?? "").trim();
    if (!id) throw new Error("psychologistUserId is required");
    try {
      return await requestFirstAvailable(
        [
          `/api/me/diary/accesses/${encodeURIComponent(id)}`,
          `/api/me/diary/access/${encodeURIComponent(id)}`,
          `/api/me/content/diary/accesses/${encodeURIComponent(id)}`,
        ],
        (path) => request(path, { method: "DELETE" }),
        "Не удалось отозвать доступ к дневнику"
      );
    } catch (error) {
      throw decorateDiaryError(error, "Не удалось отозвать доступ к дневнику");
    }
  },

  async listReadLogs(entryId, { page = 0, size = 20 } = {}) {
    const id = String(entryId ?? "").trim();
    if (!id) throw new Error("entryId is required");

    const search = new URLSearchParams();
    search.set("page", String(Math.max(0, Number(page) || 0)));
    search.set("size", String(Math.max(1, Number(size) || 20)));

    try {
      const response = await requestFirstAvailable(
        [
          `/api/me/diary/entries/${encodeURIComponent(id)}/read-logs`,
          `/api/me/content/diary/entries/${encodeURIComponent(id)}/read-logs`,
        ],
        (path) => request(`${path}?${search.toString()}`),
        "Не удалось загрузить историю чтения"
      );
      const pageLike = extractPage(response?.items ?? response);
      const meta = buildPageResult(pageLike, page, size);

      return {
        ...meta,
        items: asArray(pageLike?.content).map(normalizeDiaryReadLog),
      };
    } catch (error) {
      throw decorateDiaryError(error, "Не удалось загрузить историю чтения");
    }
  },
};
